/**
 * Workers API Service
 * Handles all worker-related API calls
 */

import { api } from './client';
import apiFormClient from './client.ts';
import { ENDPOINTS } from '../../config/api.config';
import logger from '../../utils/logger';

const getStoredOrganizationId = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('vh_user_data') || localStorage.getItem('vh_user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return (
      user?.organizationId ||
      user?.organization?._id ||
      user?.organization?.id ||
      user?.organization ||
      user?.activeOrganization ||
      user?.organizations?.find?.((org) => org?.isActive)?.organizationId ||
      user?.organizations?.[0]?.organizationId ||
      null
    );
  } catch (error) {
    logger.warn('[Workers API] Failed to parse stored user data', { error: error.message });
    return null;
  }
};

const normalizeMembershipList = (response) => {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object') {
    if (response.success === false) {
      throw new Error(response.error || 'Worker roster unavailable');
    }
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.memberships)) return response.memberships;
  }
  throw new Error('Unrecognized worker roster response');
};

const isWorkerDetail = (value) =>
  Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && (value.id != null || value._id != null || value.firstName != null || value.email != null)
  );

export const normalizeWorkerDetail = (response) => {
  if (response && typeof response === 'object') {
    if (response.success === false) {
      throw new Error(response.error || 'Worker profile unavailable');
    }
    const inner = response.success === true && response.data != null ? response.data : response;
    const worker = inner?.worker && typeof inner.worker === 'object' && !Array.isArray(inner.worker)
      ? inner.worker
      : inner;
    if (isWorkerDetail(worker)) return worker;
  }
  throw new Error('Unrecognized worker profile response');
};

/**
 * Fetch list of workers
 * @param {Object} params - Query parameters (status, jobsiteId, organizationId, etc.)
 * @returns {Promise<Object>} Workers list
 */
export async function fetchWorkers(params = {}) {
  logger.info('[Workers API] Fetching workers', { params });
  try {
    const orgId = params.organizationId || params.orgId || getStoredOrganizationId();
    if (!orgId) {
      const error = new Error('Missing organizationId for workers list');
      error.endpoint = ENDPOINTS.orgMemberships.byOrganization(':orgId');
      throw error;
    }

    // Forward jobsite/role/reportsTo/assignedForeman filters as URL query params so backend
    // can apply $or / role / reportsTo / assignedForeman pushdown
    // (vh-api routes/orgMemberships.js GET /organization/:orgId — FOREMAN-WAVE-1 item #2).
    // `status` is NOT a backend filter today (no support on OrgMembership for inactive query),
    // so it stays as a client-side post-fetch filter below.
    const qs = new URLSearchParams();
    if (params.jobsiteId) qs.set('jobsiteId', params.jobsiteId);
    if (params.role) qs.set('role', params.role);
    if (params.reportsTo) qs.set('reportsTo', params.reportsTo);
    if (params.assignedForeman) qs.set('assignedForeman', params.assignedForeman);
    const baseEndpoint = ENDPOINTS.orgMemberships.byOrganization(orgId);
    const qsString = qs.toString();
    const endpoint = qsString ? `${baseEndpoint}?${qsString}` : baseEndpoint;

    const response = await api.get(endpoint);
    let workers = normalizeMembershipList(response);

    if (params.status) {
      const normalizedStatus = String(params.status).toLowerCase();
      workers = workers.filter((membership) => {
        const employmentStatus = membership?.employmentStatus || membership?.status;
        const isActive = membership?.userId?.isActive ?? membership?.user?.isActive;
        if (normalizedStatus === 'active') {
          return employmentStatus === 'active' || isActive === true;
        }
        if (normalizedStatus === 'inactive') {
          return employmentStatus === 'inactive' || isActive === false;
        }
        return true;
      });
    }

    // Flatten nested org-membership structure into a flat worker object.
    // Backend returns { userId: { firstName, ... }, primaryJobsite: { name }, ... }
    // but components expect flat fields like { firstName, lastName, ... }.
    const resolveMembershipUser = (membership) => {
      const ref = membership.userId ?? membership.user;
      if (typeof ref === 'string' && ref) {
        return { user: {}, userId: ref };
      }
      if (ref && typeof ref === 'object') {
        return { user: ref, userId: ref._id || ref.id || null };
      }
      return { user: {}, userId: null };
    };

    const flatWorkers = workers.flatMap((m) => {
      const { user, userId } = resolveMembershipUser(m);
      if (!userId) {
        logger.warn('[Workers API] Dropping membership with unresolved user populate', {
          membershipId: m._id,
        });
        return [];
      }
      const jobsite = m.primaryJobsite || {};
      return [{
        _id: userId,
        id: userId,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        role: m.role || user.role || '',
        trade: user.workerDetails?.trade || m.trade || '',
        workerDetails: user.workerDetails || {},
        isActive: m.employmentStatus === 'active' || user.isActive === true
          ? true
          : (m.employmentStatus === 'inactive' || user.isActive === false ? false : undefined),
        jobsiteId: jobsite._id || jobsite.id || null,
        jobsiteName: jobsite.name || '',
        currentJobsite: user.currentJobsite || jobsite._id || null,
        lastCheckIn: user.lastCheckIn || user.lastLogin || null,
        riskScore: user.riskScore ?? m.riskScore ?? null,
        organizationId: user.organizationId || m.organizationId || null,
        // Preserve membership-specific fields without inventing missing measurements
        membershipId: m._id,
        employmentStatus: m.employmentStatus,
        assignedJobsites: m.assignedJobsites || [],
        reportsTo: m.reportsTo || null,
        ...(m.riskLevel != null || user.riskLevel != null
          ? { riskLevel: m.riskLevel ?? user.riskLevel }
          : {}),
        ...(m.riskProfile != null ? { riskProfile: m.riskProfile } : {}),
        ...(m.onboardingStatus != null ? { onboardingStatus: m.onboardingStatus } : {}),
        ...(m.onboarding != null ? { onboarding: m.onboarding } : {}),
        ...(m.complianceScore != null ? { complianceScore: m.complianceScore } : {}),
        ...(m.sensorVerification != null ? { sensorVerification: m.sensorVerification } : {}),
        ...(typeof user.isEmailVerified === 'boolean'
          ? { isEmailVerified: user.isEmailVerified === true }
          : {}),
      }];
    });

    return { workers: flatWorkers };
  } catch (error) {
    logger.error('[Workers API] Failed to fetch workers', { error: error.message });
    throw error;
  }
}

/**
 * Fetch worker details
 * @param {string} workerId - Worker ID
 * @returns {Promise<Object>} Worker details
 */
export async function fetchWorker(workerId) {
  logger.info('[Workers API] Fetching worker', { workerId });
  try {
    const response = await api.get(ENDPOINTS.workers.get(workerId));
    return normalizeWorkerDetail(response);
  } catch (error) {
    logger.error('[Workers API] Failed to fetch worker', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Fetch worker certifications
 * @param {string} workerId - Worker ID
 * @returns {Promise<Object>} Worker certifications
 */
export async function fetchWorkerCertifications(workerId) {
  logger.info('[Workers API] Fetching worker certifications', { workerId });
  try {
    const response = await api.get(ENDPOINTS.workers.certifications(workerId));
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch certifications', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Create a worker certification.
 * @param {string} workerId - Worker ID
 * @param {Object} data - { name, issuedBy?, certificationNumber?, issuedDate?, expiresAt?, documentUrl? }
 * @returns {Promise<Object>} Created certification envelope `{ success, data }`
 */
export async function createWorkerCertification(workerId, data) {
  logger.info('[Workers API] Creating worker certification', { workerId });
  try {
    return await api.post(ENDPOINTS.workers.certifications(workerId), data);
  } catch (error) {
    logger.error('[Workers API] Failed to create certification', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Fetch worker training records
 * @param {string} workerId - Worker ID
 * @returns {Promise<Object>} Worker training records
 */
export async function fetchWorkerTraining(workerId) {
  logger.info('[Workers API] Fetching worker training', { workerId });
  try {
    const response = await api.get(ENDPOINTS.workers.training(workerId));
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch training', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Fetch worker incidents
 * @param {string} workerId - Worker ID
 * @returns {Promise<Object>} Worker incidents
 */
export async function fetchWorkerIncidents(workerId) {
  logger.info('[Workers API] Fetching worker incidents', { workerId });
  try {
    const response = await api.get(ENDPOINTS.workers.incidents(workerId));
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch incidents', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Fetch worker attendance
 * @param {string} workerId - Worker ID
 * @returns {Promise<Object>} Worker attendance
 */
export async function fetchWorkerAttendance(workerId) {
  logger.info('[Workers API] Fetching worker attendance', { workerId });
  try {
    const response = await api.get(ENDPOINTS.workers.attendance(workerId));
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch attendance', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Fetch worker risk score
 * @param {string} workerId - Worker ID
 * @returns {Promise<Object>} Worker risk score
 */
export async function fetchWorkerRiskScore(workerId) {
  logger.info('[Workers API] Fetching worker risk score', { workerId });
  try {
    // Use ML endpoint for risk scoring
    const response = await api.post('/api/ml/score_risk', {
      userId: workerId,
      type: 'worker',
    });
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch risk score', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Fetch workers for a specific jobsite
 * @param {string} jobsiteId - Jobsite ID
 * @returns {Promise<Object>} Workers list for jobsite
 */
export async function fetchJobsiteWorkers(jobsiteId) {
  logger.info('[Workers API] Fetching jobsite workers', { jobsiteId });
  try {
    const response = await api.get(ENDPOINTS.projects.workers(jobsiteId));
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch jobsite workers', { jobsiteId, error: error.message });
    throw error;
  }
}

/**
 * Fetch compliance summary
 * @returns {Promise<Object>} Compliance summary
 */
export async function fetchComplianceSummary() {
  logger.info('[Workers API] Fetching compliance summary');
  try {
    const response = await api.get(ENDPOINTS.compliance.workforceSummary);
    if (response && typeof response === 'object') {
      if (response.success === false) {
        throw new Error(response.error || 'Compliance summary unavailable');
      }
      if (
        response.success === true
        && response.data != null
        && typeof response.data === 'object'
        && !Array.isArray(response.data)
      ) {
        return response.data;
      }
    }
    throw new Error('Unrecognized compliance summary response');
  } catch (error) {
    logger.error('[Workers API] Failed to fetch compliance summary', { error: error.message });
    throw error;
  }
}

/**
 * Fetch worker activity feed
 * @param {string} workerId - Worker ID
 * @param {Object} params - Query parameters (limit, since)
 */
export async function fetchWorkerActivity(workerId, params = {}) {
  logger.info('[Workers API] Fetching worker activity', { workerId, params });
  try {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `${ENDPOINTS.workers.activity(workerId)}${queryString ? `?${queryString}` : ''}`;
    return await api.get(endpoint);
  } catch (error) {
    logger.error('[Workers API] Failed to fetch worker activity', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Fetch certification summary for organization
 */
export async function fetchCertificationsSummary() {
  logger.info('[Workers API] Fetching certifications summary');
  try {
    const response = await api.get(ENDPOINTS.workers.certificationsSummary);
    if (response && typeof response === 'object') {
      if (response.success === false) {
        throw new Error(response.error || 'Certifications summary unavailable');
      }
      if (
        response.success === true
        && response.data != null
        && typeof response.data === 'object'
        && !Array.isArray(response.data)
      ) {
        return response.data;
      }
    }
    throw new Error('Unrecognized certifications summary response');
  } catch (error) {
    logger.error('[Workers API] Failed to fetch certifications summary', { error: error.message });
    throw error;
  }
}

const isFiniteImportCount = (value) => value != null && value !== '' && Number.isFinite(Number(value));

const isImportPreviewData = (value) => (
  Boolean(value)
  && typeof value === 'object'
  && !Array.isArray(value)
  && Array.isArray(value.rows)
  && value.totals
  && typeof value.totals === 'object'
  && !Array.isArray(value.totals)
  && isFiniteImportCount(value.totals.valid)
  && isFiniteImportCount(value.totals.warning)
  && isFiniteImportCount(value.totals.error)
);

const isImportResultData = (value) => (
  Boolean(value)
  && typeof value === 'object'
  && !Array.isArray(value)
  && isFiniteImportCount(value.createdCount)
  && Array.isArray(value.warnings)
  && Array.isArray(value.errors)
);

/** Already-unwrapped preview data (BulkImportPanel second pass after the service unwraps). */
export function recognizeImportPreviewData(value) {
  if (isImportPreviewData(value)) return value;
  throw new Error('Unrecognized import preview response');
}

/** Already-unwrapped import result (BulkImportPanel second pass after the service unwraps). */
export function recognizeImportResultData(value) {
  if (isImportResultData(value)) return value;
  throw new Error('Unrecognized import response');
}

/** Producer: POST /api/workers/import/preview → {success:true,data:{rows,totals}} */
export function recognizeImportPreview(response) {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    throw new Error('Unrecognized import preview response');
  }
  if (response.success === false) {
    throw new Error(response.error || 'Import preview unavailable');
  }
  if (response.success === true && isImportPreviewData(response.data)) {
    return response.data;
  }
  throw new Error('Unrecognized import preview response');
}

/** Producer: POST /api/workers/import → {success:true,data:{createdCount,warnings,errors}} */
export function recognizeImportResult(response) {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    throw new Error('Unrecognized import response');
  }
  if (response.success === false) {
    throw new Error(response.error || 'Worker import unavailable');
  }
  if (response.success === true && isImportResultData(response.data)) {
    return response.data;
  }
  throw new Error('Unrecognized import response');
}

/**
 * Preview worker import from CSV
 * @param {File} file - CSV file
 */
export async function previewWorkerImport(file) {
  logger.info('[Workers API] Previewing worker import', { fileName: file?.name });
  const formData = new FormData();
  formData.append('csvFile', file);
  try {
    const response = await apiFormClient.postForm(ENDPOINTS.workers.importPreview, formData);
    return recognizeImportPreview(response);
  } catch (error) {
    logger.error('[Workers API] Failed to preview worker import', { error: error.message });
    throw error;
  }
}

/**
 * Import workers from CSV
 * @param {File} file - CSV file
 */
export async function importWorkers(file) {
  logger.info('[Workers API] Importing workers', { fileName: file?.name });
  const formData = new FormData();
  formData.append('csvFile', file);
  try {
    const response = await apiFormClient.postForm(ENDPOINTS.workers.import, formData);
    return recognizeImportResult(response);
  } catch (error) {
    logger.error('[Workers API] Failed to import workers', { error: error.message });
    throw error;
  }
}

/**
 * Update worker profile
 * @param {string} workerId - Worker ID
 * @param {Object} data - Profile data (firstName, lastName, phone, workerDetails, etc.)
 * @returns {Promise<Object>} Updated worker
 */
export async function updateWorker(workerId, data) {
  logger.info('[Workers API] Updating worker profile', { workerId });
  try {
    return await api.patch(ENDPOINTS.workers.update(workerId), data);
  } catch (error) {
    logger.error('[Workers API] Failed to update worker', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Update worker assignment
 * @param {string} workerId - Worker ID
 * @param {Object} data - Assignment payload
 */
export async function updateWorkerAssignment(workerId, data) {
  logger.info('[Workers API] Updating worker assignment', { workerId });
  try {
    return await api.patch(ENDPOINTS.workers.assignment(workerId), data);
  } catch (error) {
    logger.error('[Workers API] Failed to update assignment', { workerId, error: error.message });
    throw error;
  }
}

/**
 * Invite a worker
 * @param {Object} inviteData - Invite data (email, jobsiteId, role, etc.)
 * @returns {Promise<Object>} Invite response
 */
export async function inviteWorker(inviteData) {
  logger.info('[Workers API] Inviting worker', { email: inviteData.email });
  try {
    const response = await api.post(ENDPOINTS.invites.workerCreate, inviteData);
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to invite worker', { error: error.message });
    throw error;
  }
}

/**
 * Fetch pending worker invites
 * @returns {Promise<Object>} Worker invites list
 */
export async function fetchWorkerInvites() {
  logger.info('[Workers API] Fetching worker invites');
  try {
    const response = await api.get(ENDPOINTS.invites.workerList);
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch worker invites', { error: error.message });
    throw error;
  }
}

/**
 * Fetch worker summary for dashboard
 * Returns aggregated statistics including worker counts, role distribution,
 * jobsite distribution, recently added workers, and compliance stats
 * @returns {Promise<Object>} Worker summary data
 */
export async function fetchWorkerSummary() {
  logger.info('[Workers API] Fetching worker summary');
  try {
    const response = await api.get(ENDPOINTS.workers.summary);
    return response;
  } catch (error) {
    logger.error('[Workers API] Failed to fetch worker summary', { error: error.message });
    throw error;
  }
}

export async function fetchMyWorkerProfile() {
  logger.info('[Workers API] Fetching current worker profile');
  try {
    return await api.get(ENDPOINTS.workers.meProfile);
  } catch (error) {
    logger.error('[Workers API] Failed to fetch current worker profile', { error: error.message });
    throw error;
  }
}

/**
 * Fetch the authenticated worker's certifications.
 * GET /api/workers/me/certifications — does not require a Mongo worker _id.
 * @returns {Promise<Object>} `{ success, data: { certifications: { active, expiringSoon, expired }, totalCount } }`
 */
export async function fetchMyWorkerCertifications() {
  logger.info('[Workers API] Fetching current worker certifications');
  try {
    return await api.get(ENDPOINTS.workers.meCertifications);
  } catch (error) {
    logger.error('[Workers API] Failed to fetch current worker certifications', { error: error.message });
    throw error;
  }
}

export default {
  fetchWorkers,
  fetchWorker,
  fetchWorkerCertifications,
  createWorkerCertification,
  fetchWorkerTraining,
  fetchWorkerIncidents,
  fetchWorkerAttendance,
  fetchWorkerRiskScore,
  fetchJobsiteWorkers,
  fetchComplianceSummary,
  fetchWorkerActivity,
  fetchCertificationsSummary,
  previewWorkerImport,
  importWorkers,
  updateWorker,
  updateWorkerAssignment,
  inviteWorker,
  fetchWorkerInvites,
  fetchWorkerSummary,
  fetchMyWorkerProfile,
  fetchMyWorkerCertifications,
};
